import type { FaqDocument, FaqTocEntry } from './types';

export const faqDocument: FaqDocument = {
  title: 'Frequently Asked Questions',
  eyebrow: 'Help · FAQs',
  summary:
    'Answers about GoGrabJob — job discovery and matching, resume tailoring, ATS scoring, pricing in INR, privacy, and how to get the most from your job search workspace.',
  categories: [
    {
      id: 'getting-started',
      title: 'Getting started',
      items: [
        {
          id: 'gs-free',
          question: 'Is GoGrabJob free to try?',
          answer:
            'Yes. The Free plan includes 3 lifetime AI resume optimizations — no credit card required. Create an account, upload or paste your resume, and run your first optimization in minutes.',
        },
        {
          id: 'gs-start',
          question: 'How do I get started?',
          answer:
            'Sign up at gograbjob.com, upload or paste your existing resume, and describe the roles you want. The AI agent surfaces ranked matches from Naukri, LinkedIn, and more — then helps you tailor your resume and score ATS fit for each application in the same workspace.',
        },
        {
          id: 'gs-account',
          question: 'Do I need to install anything?',
          answer:
            'No install required for the web app — it works in Chrome, Safari, Edge, and Firefox on desktop and mobile. Native iOS and Android apps are also available for on-the-go use.',
        },
        {
          id: 'gs-languages',
          question: 'Which languages are supported?',
          answer:
            'The product UI supports multiple languages via the language selector. Resume optimization works best in English; we are expanding support for additional languages over time.',
        },
      ],
    },
    {
      id: 'optimization-ats',
      title: 'Optimization & ATS',
      items: [
        {
          id: 'opt-how',
          question: 'How does GoGrabJob optimize my resume?',
          answer:
            'We parse your existing resume and the target job description, extract required skills and keywords, and use AI to suggest edits for better ATS alignment while keeping your experience accurate. You review every change before downloading or applying — we do not create a résumé from scratch.',
        },
        {
          id: 'opt-ats',
          question: 'What is ATS scoring and how accurate is it?',
          answer:
            'ATS (Applicant Tracking System) scoring estimates how well your resume matches a job\'s keywords and structure. Our score reflects skills overlap, formatting signals, and common parser patterns. It is a guide — not a guarantee of interview outcomes — but users typically see meaningful score improvements after optimization.',
        },
        {
          id: 'opt-time',
          question: 'How long does an optimization take?',
          answer:
            'Most optimizations complete in 2–5 minutes depending on resume length and job description complexity. Progress is shown in real time inside the workspace.',
        },
        {
          id: 'opt-chatgpt',
          question: 'How is this different from ChatGPT?',
          answer:
            'Chat tools give one-off rewrites with no memory of your applications. GoGrabJob ties each optimized version to a specific job, stores scores and history, tracks applications, and runs discovery → optimize → apply → prep in one pipeline.',
        },
        {
          id: 'opt-formats',
          question: 'What file formats can I upload?',
          answer:
            'PDF, DOC, DOCX, and TXT up to 10 MB. PDF or DOCX usually gives the best parsing results.',
        },
      ],
    },
    {
      id: 'pricing-billing',
      title: 'Pricing & billing',
      items: [
        {
          id: 'price-plans',
          question: 'What plans are available?',
          answer:
            'Free (3 lifetime optimizations), Monthly (₹349 — 60 optimizations/month), Quarterly (₹749 — 180 total), and Half-Yearly (₹1,499 — 360 total). Paid plans include Premium AI, unlimited PDF downloads, and full application tracking. See the Pricing page for the latest feature matrix.',
        },
        {
          id: 'price-optimization',
          question: 'What counts as one optimization?',
          answer:
            'Each time you tailor your resume to a specific job description — including ATS scoring and keyword alignment — that uses one optimization from your plan allowance.',
        },
        {
          id: 'price-cancel',
          question: 'Can I cancel my subscription?',
          answer:
            'Yes, anytime from Settings → Billing. Cancellation stops future charges; you keep paid features until the end of the current billing period, then your account moves to the Free plan. Your data remains accessible.',
        },
        {
          id: 'price-refund',
          question: 'Can I get a refund?',
          answer:
            'Subscription payments are generally non-refundable once the billing period has started. We may refund duplicate charges or extended technical failures — see our Refund & Cancellation Policy or email support@gograbjob.com within 7 days of a charge.',
        },
        {
          id: 'price-inr',
          question: 'Why are prices shown in INR?',
          answer:
            'GoGrabJob is built in India with India-native pricing. We may show approximate local equivalents for your locale, but checkout is processed in INR via Cashfree or Razorpay unless otherwise stated.',
        },
      ],
    },
    {
      id: 'features',
      title: 'Features & workspace',
      items: [
        {
          id: 'feat-track',
          question: 'Can I track my job applications?',
          answer:
            'Yes. The application tracker lets you log roles, statuses, notes, and follow-ups. Paid plans include unlimited entries; the Free plan includes a limited number of tracker slots.',
        },
        {
          id: 'feat-cover',
          question: 'Does GoGrabJob write cover letters?',
          answer:
            'Paid plans include AI cover letter generation tailored to the same job description as your optimized resume. Always review before sending.',
        },
        {
          id: 'feat-jobs',
          question: 'Where does GoGrabJob find jobs?',
          answer:
            'We ingest and rank roles from boards including Naukri, LinkedIn, Indeed, Peerlist, and Internshala — matched to your profile rather than generic keyword spam.',
        },
        {
          id: 'feat-history',
          question: 'Is my optimization history saved?',
          answer:
            'Yes. Each version is stored in your workspace so you can compare scores, revert, or reuse content for similar roles. Paid plans include full history; Free includes a limited window.',
        },
        {
          id: 'feat-interview',
          question: 'Do you help with interview prep?',
          answer:
            'The workspace includes interview-prep tools tied to your target role — likely questions, talking points, and gap analysis based on the job description you are pursuing.',
        },
      ],
    },
    {
      id: 'privacy-security',
      title: 'Privacy & security',
      items: [
        {
          id: 'priv-data',
          question: 'Is my resume data private?',
          answer:
            'Your resume and application data are encrypted in transit (TLS) and at rest. We do not sell your personal information to recruiters or data brokers. You can export or delete your account from Settings.',
        },
        {
          id: 'priv-share',
          question: 'Do you share my resume with employers?',
          answer:
            'Only when you explicitly choose to apply or share materials. GoGrabJob does not auto-submit applications on your behalf without your action.',
        },
        {
          id: 'priv-ai',
          question: 'Is my resume used to train AI models?',
          answer:
            'We use AI subprocessors to deliver your requested outputs under strict terms. We do not use your resume content to train public third-party models without your consent.',
        },
        {
          id: 'priv-rights',
          question: 'What are my data rights?',
          answer:
            'Depending on your location you may access, correct, export, or delete your data. Indian users: see our DPDP Compliance page. EEA/UK users: see GDPR Compliance. Contact support@gograbjob.com for requests.',
        },
      ],
    },
    {
      id: 'business-support',
      title: 'Business & support',
      items: [
        {
          id: 'biz-b2b',
          question: 'Do you offer a product for companies or universities?',
          answer:
            'Yes. GoGrabJob for Business serves teams, consultancies, and career centres with bulk access and admin tools. Visit our Business page or contact support@gograbjob.com for enterprise pricing.',
        },
        {
          id: 'biz-support',
          question: 'How do I contact support?',
          answer:
            'Email support@gograbjob.com or use Contact Us on the website. We aim to respond within one business day. Signed-in users can also reach support from the in-app help section.',
        },
        {
          id: 'biz-affiliate',
          question: 'Is GoGrabJob affiliated with GrabJobs or job boards?',
          answer:
            'No. GoGrabJob is an independent product by Zykes Technologies LLP. We are not affiliated with GrabJobs, Naukri, LinkedIn, or any job board — we integrate with public listings to help you discover roles.',
        },
      ],
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      items: [
        {
          id: 'ts-fail',
          question: 'My optimization failed or timed out. What should I do?',
          answer:
            'Refresh the page and retry with a shorter job description or a cleaner PDF export. If the issue persists, email support@gograbjob.com with your account email and approximate time of the error.',
        },
        {
          id: 'ts-payment',
          question: 'I was charged but my plan did not activate.',
          answer:
            'Payments can take a few minutes to sync. Log out and back in, then check Settings → Billing. If still missing, email support@gograbjob.com with your transaction ID from the payment receipt.',
        },
        {
          id: 'ts-login',
          question: 'I cannot log in or reset my password.',
          answer:
            'Use Forgot password on the login page. Check spam for the reset email. If you signed up with Google or phone OTP, use the same method you registered with. Contact support if you are locked out.',
        },
      ],
    },
  ],
};

export function buildFaqToc(categories: FaqDocument['categories']): FaqTocEntry[] {
  return categories.map((category) => ({
    id: category.id,
    label: category.title,
    level: 2 as const,
  }));
}

export function buildFaqPageJsonLd(doc: FaqDocument = faqDocument) {
  const items = doc.categories.flatMap((c) => c.items);
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
}

export type { FaqDocument, FaqCategory, FaqItem, FaqTocEntry };
